import React, {useState, useEffect} from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import {useVsiAppContext} from "../stores/VsiApp";
import styles from "../utils/themeStyles";
import {genericColors} from "../utils/chartUtils";
import {withErrorBoundary} from "../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";

require("highcharts/indicators/indicators")(Highcharts);
require("highcharts/indicators/pivot-points")(Highcharts);
require("highcharts/indicators/macd")(Highcharts);
require("highcharts/modules/exporting")(Highcharts);
require("highcharts/modules/map")(Highcharts);

function StackedColumnChart(
    {
        categories,
        data,
        series,
        title = "",
        height,
        colors,
        disableLegend,
        changeAxisTitle,
        yAxisTitle,
        scrollHeight,
        dataLabelsEnabled,
        stackedLabelsEnabled
    }) {
    const {theme, isTabletScreen} = useVsiAppContext();
    const basicTextStyle = {
        color: styles[theme].textColor,
        fontFamily: styles[theme].fontFamily,
    };

    const updateSeries = (series, data) => {
        if (series && colors) {
            return series.map((obj, i) => {
                if (!obj.color) {
                    return {
                        ...obj,
                        color: colors[i]
                    }
                } else {
                    return obj
                }
            })
        } else if (series) return series;
        else if (data)
            return [
                {
                    data: data.map((datum, i) => ({
                        y: datum,
                        color: colors?.[i] || genericColors?.[i] || genericColors?.[i - data.length + 1]
                    })),
                },
            ];
        return [];
    };

    const [chartOptions, setChartOptions] = useState({
        colors: colors || genericColors,
        mapNavigation: {
            enabled: false,
        },
        credits: {
            enabled: false,
        },
        tooltip: {
            backgroundColor: styles[theme].altBgColor,
            style: basicTextStyle,
        },
        xAxis: {
            categories,
            labels: {
                style: basicTextStyle,
                autoRotation: [-45],
                step: 1
            },
            scrollbar: {
                enabled: true
            },
            min: 0,
            max: series?.[0]?.data?.length > 100 ? 100 : null,
        },
        yAxis: {
            stackLabels: {
                enabled: typeof stackedLabelsEnabled === 'boolean' ? stackedLabelsEnabled : true,
                style: {
                    color: styles[theme].textColor,
                    textOutline: "none"
                },
                formatter: function () {
                    return Highcharts.numberFormat(this.total,2);
                }
            },
            title: {
                text: yAxisTitle || changeAxisTitle ? "GiB" : "Size",
                style: {
                    ...basicTextStyle,
                    fontWeight: 'bold'
                }
            },
            labels: {
                style: basicTextStyle,
                allowOverlap: true,
                enabled: true
            }
        },
        chart: {
            type: "column",
            // backgroundColor: styles[theme].cardBgColor,
            backgroundColor: 'transparent',
            color: styles[theme].textColor,
            height: height || 500,
            zoomType: false
        },
        title: {
            text: title,
            style: basicTextStyle,
        },
        labels: {
            style: basicTextStyle,
        },
        legend: {
            itemStyle: basicTextStyle,
            align: "center",
            enabled: !disableLegend,
        },
        plotOptions: {
            series: {
                stacking: 'normal'
            },
            column: {
                pointPadding: 0.2,
                borderWidth: 0,
                dataLabels: {
                    enabled: dataLabelsEnabled,
                    color: styles[theme].textColor,
                    style: {
                        textOutline: "none"
                    },
                    formatter: function () {
                        return Highcharts.numberFormat(this.y, 2);
                    }
                }
            }
        },

        series: updateSeries(series, data),
    });

    useEffect(() => {
        setChartOptions({
            ...chartOptions,
            colors: colors || genericColors,
            xAxis: {
                ...chartOptions.xAxis,
                categories,
                labels: {
                    ...chartOptions.xAxis.labels,
                    style: basicTextStyle,
                },
                min: 0,
                tickLength: 0,
                scrollbar: {
                    enabled: true
                }
            },
            tooltip: {
                backgroundColor: styles[theme].altBgColor,
                style: basicTextStyle,
            },
            yAxis: {
                ...chartOptions.yAxis,
                stackLabels: {
                    ...chartOptions.yAxis.stackLabels,
                    enabled: typeof stackedLabelsEnabled === 'boolean' ? stackedLabelsEnabled : true,
                    style: {
                        ...chartOptions.yAxis.stackLabels.style,
                        color: styles[theme].textColor,
                    },
                },
                title: {
                    ...chartOptions.yAxis.title,
                    text: yAxisTitle || chartOptions.yAxis.title.text,
                    style: {
                        ...basicTextStyle,
                        fontWeight: '600'
                    }
                },
                labels: {
                    ...chartOptions.yAxis.labels,
                    style: basicTextStyle,
                }
            },
            chart: {
                ...chartOptions.chart,
                color: styles[theme].textColor,
                scrollablePlotArea: {
                    minHeight: scrollHeight || 0,
                    scrollPositionX: 1
                },
                // backgroundColor: styles[theme].cardBgColor,
                height: height || 500
            },
            title: {
                ...chartOptions.title,
                text: title,
                style: basicTextStyle,
            },
            labels: {
                ...chartOptions.labels,
                style: basicTextStyle,
            },
            legend: {
                ...chartOptions.legend,
                itemStyle: basicTextStyle,
                align: "center",
                enabled: !disableLegend
            },
            plotOptions: {
                ...chartOptions.plotOptions,
                column: {
                ...chartOptions.column,
                dataLabels: {
                    ...chartOptions.plotOptions.column.dataLabels,
                    enabled: dataLabelsEnabled,
                }
            }
            },
            series: updateSeries(series, data),
        });
        // eslint-disable-next-line
    }, [
        categories,
        series,
        data,
        theme,
        isTabletScreen,
        dataLabelsEnabled,
        disableLegend,
        yAxisTitle,
        changeAxisTitle,
        scrollHeight,
        height,
        title,
        colors,
        categories,
        stackedLabelsEnabled
    ]);

    return <HighchartsReact highcharts={Highcharts} options={chartOptions}/>;
}

StackedColumnChart.displayName = "StackedColumnChart"

export default withErrorBoundary(StackedColumnChart, GeneralErrorBoundary);
